import { Component, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe, DecimalPipe } from '@angular/common';
import { LogisticService } from '@app/logistic/logistic.service';
import { AlertService } from '@app/shared/alert.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { SignaturePadComponent } from '@app/shared/signature-pad/signature-pad.component';
import { RentalDirectionComponent, rentalDirection } from '@app/shared/rental-direction/rental-direction.component';
import { serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { orderLineNamed, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';

export interface DeliveryValidationData {
  order: any;          // commande (N° commande affiché dans l'entête)
  deliveryFormGroup: FormGroup; // livraison active prête (qtés livrées + beforeSubmit), postée depuis ce dialog
  customer: any;       // Partner lié
  lines: any[];        // lignes du BL déjà limitées aux qtés livrées (net recalculé)
  amount_taxi: number; // total TTC du BL (bases par taux + TVA arrondie une fois par taux)
  amount_taxx: number; // total HT du BL (Σ net_taxx arrondie par taux)
  collectOnly?: boolean; // passage de location : rend { signature, note } sans rien poster, l'appelant signe chaque BL du passage
}

@Component({
  selector: 'app-delivery-validation',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, DatePipe, DecimalPipe, SignaturePadComponent, RentalDirectionComponent],
  templateUrl: './delivery-validation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./delivery-validation.component.scss'],
})
export class DeliveryValidationComponent {
  // Le trait vit dans le pad COMMUN (shared/signature-pad) : lissage, validité, image.
  // Ce dialog garde ce qui est à lui — le BL affiché, le libellé, les boutons, le POST.
  @ViewChild(SignaturePadComponent) pad!: SignaturePadComponent;

  public today: Date = new Date();
  public posting = false;   // garde anti double-submit pendant le POST de validation

  constructor(
    public dialogRef: MatDialogRef<DeliveryValidationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeliveryValidationData,
    private logisticService: LogisticService,
    private alertService: AlertService,
    private spin: SpinnerService,
  ) {}

  signatureValid(): boolean {
    return !!this.pad?.signatureValid();
  }

  get order(): any { return this.data?.order; }
  get customer(): any { return this.data?.customer; }
  get amountTaxi(): number { return +(this.data?.amount_taxi ?? 0); }
  get amountTaxx(): number { return +(this.data?.amount_taxx ?? 0); }

  // Lignes du BL : déjà limitées aux quantités livrées de la livraison active (construites côté DeliveryComponent)
  get lines(): any[] {
    return this.data?.lines ?? [];
  }

  // Ligne louée : l'intitulé de son contrat de location, l'article dessous, comme sur le BL imprimé.
  title(line: any): string { return rentalTitle(orderLineNamed(line)) ?? '—'; }
  article(line: any): string | null { return rentalSubtitle(orderLineNamed(line)); }

  // Place de la flèche réservée seulement si le BL porte une ligne de location : les libellés restent alignés, un BL F2 est inchangé.
  get hasRentalDirection(): boolean {
    return this.lines.some(line => rentalDirection(line?.typ, line?.bl_qty) !== null);
  }

  // Reprise de matériel (ligne louée à −1 sur ce BL, bl_qty de DeliveryComponent.buildDeliveryNoteLines) : ligne en rouge pâle.
  isReprise(line: any): boolean {
    return rentalDirection(line?.typ, line?.bl_qty) === 'reprise';
  }

  get customerLabel(): string {
    const c = this.customer;
    if (!c) return 'Non renseigné';
    const label = `${c.firstname ?? ''} ${c.name ?? ''}`.trim();
    return label || 'Non renseigné';
  }

  clearSignature(): void {
    this.pad?.clear();
  }

  onCancel(): void { this.dialogRef.close(null); }
  onValidate(): void {
    if (this.posting || !this.signatureValid()) return;
    this.posting = true;
    this.dialogRef.disableClose = true;  // Échap pendant le POST fermerait sans rafraîchir un BL que le serveur signe quand même
    const signature = this.pad.toDataURL();
    const note = this.pad.noteText();		// remarque facultative : le serveur l'imprime dans le pavé de signature du BL
    if (this.data.collectOnly) { this.dialogRef.close({ signature, note }); return; }
    // Appel serveur AVANT de fermer : on ne ferme le dialog que si la validation réussit.
    this.logisticService.postDeliveryValidation(this.data.deliveryFormGroup, signature, note).pipe(this.spin.block('Validation de la livraison…')).subscribe({
      next: (response: any) => this.dialogRef.close(response ?? true),
      error: (e: any) => {
        this.posting = false;
        this.dialogRef.disableClose = false;
        this.alertService.error('La validation de livraison a échoué : ' + serverMessage(e).replace(/^Process Error:\s*/, ''));
        console.error(e?.error);
      },
    });
  }
}
